/**
 * Me · More — link grid for everything not on bottom-nav
 */
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  User, Wallet, FileText, Megaphone, Ticket, Banknote, Gift, Award, Trophy,
  Briefcase, ClipboardCheck, CheckSquare, Monitor, LogOut, ArrowRight,
} from "lucide-react";

const ITEMS = [
  { to: "/my-portal/profile", label: "My Profile", icon: User, tone: "indigo" },
  { to: "/my-portal/salary", label: "Salary Slips", icon: Wallet, tone: "emerald" },
  { to: "/my-portal/announcements", label: "Announcements", icon: Megaphone, tone: "amber" },
  { to: "/my-portal/documents", label: "My Documents", icon: FileText, tone: "rose" },
  { to: "/my-portal/holidays", label: "Holidays", icon: Gift, tone: "violet" },
  { to: "/my-portal/requests", label: "Service Requests", icon: Ticket, tone: "sky" },
  { to: "/my-portal/advances", label: "Salary Advance", icon: Banknote, tone: "emerald" },
  { to: "/my-portal/festivals", label: "Festival Gifts", icon: Gift, tone: "amber" },
  { to: "/my-portal/appraisal", label: "Appraisal", icon: Award, tone: "indigo" },
  { to: "/my-portal/performance", label: "My Performance", icon: Trophy, tone: "rose" },
  { to: "/my-portal/assets", label: "My Assets", icon: Briefcase, tone: "slate" },
];

// Manager-only extras
const MGR_ITEMS = [
  { to: "/my-portal/approvals", label: "Team Approvals", icon: CheckSquare, tone: "emerald" },
  { to: "/delegation/create", label: "Delegate Tasks", icon: ClipboardCheck, tone: "indigo" },
];

const TONES = {
  indigo: "bg-indigo-50 text-indigo-900 ring-indigo-200",
  emerald: "bg-emerald-50 text-emerald-900 ring-emerald-200",
  rose: "bg-red-50 text-red-900 ring-red-200",
  sky: "bg-sky-50 text-sky-900 ring-sky-200",
  amber: "bg-amber-50 text-amber-900 ring-amber-200",
  violet: "bg-violet-50 text-violet-900 ring-violet-200",
  slate: "bg-slate-100 text-slate-900 ring-slate-300",
};

const ASSIGNER_ROLES = ["senior_management", "hod", "manager", "ea"];

export default function MeMore() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.is_senior_management ||
                    user?.role === "hod" ||
                    ASSIGNER_ROLES.includes(user?.role);
  return (
    <div className="space-y-3" data-testid="me-more">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">More</div>
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Other Tools</h1>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Signed in as <strong>{user?.name}</strong>
        </p>
      </div>

      <div className="space-y-2">
        {ITEMS.map((it) => <Row key={it.to} {...it} navigate={navigate} />)}
      </div>

      {isManager && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-4 mb-2 px-1">
            Manager
          </div>
          <div className="space-y-2">
            {MGR_ITEMS.map((it) => <Row key={it.to} {...it} navigate={navigate} />)}
          </div>
        </>
      )}

      <button
        onClick={() => navigate("/my-portal/dashboard")}
        data-testid="me-desktop-link"
        className="w-full mt-4 flex items-center gap-3 p-3 rounded-xl ring-1 ring-slate-300 bg-white"
      >
        <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
          <Monitor className="h-5 w-5 text-slate-700" />
        </div>
        <div className="flex-1 text-left text-sm font-semibold text-slate-700">
          Open Desktop View
        </div>
        <ArrowRight className="h-4 w-4 opacity-60" />
      </button>

      <button
        onClick={async () => { await logout(); navigate("/login"); }}
        data-testid="me-more-logout"
        className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-xl ring-1 ring-red-200 bg-red-50 text-red-700 font-semibold"
      >
        <LogOut className="h-4 w-4" /> Logout
      </button>
    </div>
  );
}

function Row({ to, label, icon: Icon, tone, navigate }) {
  return (
    <button
      onClick={() => navigate(to)}
      data-testid={`me-more-${to.replace(/\//g, "-")}`}
      className={`w-full flex items-center gap-3 p-3 rounded-xl ring-1 transition hover:opacity-90 ${TONES[tone]}`}
    >
      <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 text-left text-sm font-semibold">{label}</div>
      <ArrowRight className="h-4 w-4 opacity-60" />
    </button>
  );
}
